import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, Calendar, AlertCircle, Plus, CheckCircle, XCircle, Clock } from 'lucide-react';

const GoalsDashboard = () => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/intelligence/c8/goals`, {
        credentials: 'include'
      });

      const result = await response.json();
      if (result.success) {
        setGoals(result.goals || []);
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'achieved':
        return 'border-green-500/30 bg-green-500/10';
      case 'failed':
        return 'border-red-500/30 bg-red-500/10';
      case 'active':
        return 'border-cyan-500/30 bg-cyan-500/10';
      default:
        return 'border-slate-500/30 bg-slate-500/10';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'achieved':
        return <CheckCircle className="text-green-400" size={20} />;
      case 'failed':
        return <XCircle className="text-red-400" size={20} />;
      case 'active':
        return <Clock className="text-cyan-400" size={20} />;
      default:
        return <AlertCircle className="text-slate-400" size={20} />;
    }
  };

  const calculateProgress = (goal) => {
    return Math.min((goal.current_value / goal.target_value) * 100, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="border-b border-white/5 backdrop-blur-xl bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Target className="inline mr-3 text-cyan-400" size={36} />
                Business Goals
              </h1>
              <p className="text-slate-400 text-lg">Suivez et atteignez vos objectifs business</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-200"
              data-testid="create-goal-button"
            >
              <Plus size={20} className="inline mr-2" />
              Créer un objectif
            </motion.button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total', value: goals.length, icon: Target, color: 'cyan' },
            { label: 'Actifs', value: goals.filter(g => g.status === 'active').length, icon: Clock, color: 'blue' },
            { label: 'Atteints', value: goals.filter(g => g.status === 'achieved').length, icon: CheckCircle, color: 'green' },
            { label: 'Échoués', value: goals.filter(g => g.status === 'failed').length, icon: XCircle, color: 'red' }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">{stat.label}</span>
                <stat.icon className={`text-${stat.color}-400`} size={20} />
              </div>
              <div className="text-3xl font-bold text-white">{stat.value}</div>
            </motion.div>
          ))}
        </div>

        {/* Goals Grid */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Chargement...</div>
        ) : goals.length === 0 ? (
          <div className="text-center py-12">
            <Target className="mx-auto mb-4 text-slate-600" size={48} />
            <p className="text-slate-400 text-lg mb-4">Aucun objectif créé</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-cyan-500/10 text-cyan-400 rounded-xl border border-cyan-500/30 hover:bg-cyan-500/20 transition-all"
            >
              Créer votre premier objectif
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {goals.map((goal, index) => {
              const progress = calculateProgress(goal);
              const daysRemaining = Math.ceil((new Date(goal.end_date) - new Date()) / (1000 * 60 * 60 * 24));

              return (
                <motion.div
                  key={goal.goal_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className={`backdrop-blur-xl rounded-2xl border p-6 cursor-pointer ${getStatusColor(goal.status)}`}
                  data-testid={`goal-card-${goal.goal_id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-white font-semibold text-lg mb-1">{goal.title}</div>
                      <div className="text-slate-400 text-sm">{goal.type}</div>
                    </div>
                    {getStatusIcon(goal.status)}
                  </div>

                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Progression</span>
                      <span className="text-white font-semibold">{progress.toFixed(1)}%</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                        className="h-full bg-gradient-to-r from-cyan-400 to-blue-400"
                      />
                    </div>
                  </div>

                  {/* Values */}
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <div className="text-2xl font-bold text-white">{goal.current_value}</div>
                      <div className="text-slate-400 text-xs">sur {goal.target_value} {goal.unit}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm ${daysRemaining > 0 ? 'text-cyan-400' : 'text-red-400'}`}>
                        {daysRemaining > 0 ? `${daysRemaining}j restants` : 'Échéance passée'}
                      </div>
                    </div>
                  </div>

                  {/* Platforms */}
                  {goal.platforms && goal.platforms.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {goal.platforms.map(platform => (
                        <span
                          key={platform}
                          className="px-2 py-1 bg-white/5 rounded-md text-xs text-slate-300"
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal - Simplified */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 rounded-2xl border border-white/10 p-8 max-w-md w-full mx-4"
          >
            <h2 className="text-2xl font-bold text-white mb-4">Créer un objectif</h2>
            <p className="text-slate-400 mb-6">
              Fonctionnalité disponible prochainement. Utilisez l'API directement pour créer des objectifs.
            </p>
            <button
              onClick={() => setShowCreateModal(false)}
              className="w-full px-6 py-3 bg-cyan-500/10 text-cyan-400 rounded-xl border border-cyan-500/30 hover:bg-cyan-500/20 transition-all"
            >
              Fermer
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default GoalsDashboard;
